import { useEffect, useMemo, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { EmptyState } from '../../components/EmptyState.js'
import { ErrorState } from '../../components/ErrorState.js'
import { LoadingState } from '../../components/LoadingState.js'
import { Topbar } from '../../components/Topbar.js'
import { useAuth } from '../../lib/auth.js'
import { formatCount } from '../../lib/format.js'
import { usePageTitle } from '../../lib/page-title.js'
import { useDebounced } from './useTasks.js'
import type { Workload } from './useTasks.js'
import { Avatar, Button, Chip, Input, Text } from '../../ui/index.js'

interface TeamPerson {
  readonly id: string
  readonly name: string
  readonly role: string
  readonly jobTitle: string | null
  readonly departmentId: string | null
  readonly isActive: boolean
}

interface Department {
  readonly id: string
  readonly name: string
}

interface NextTaskPage {
  readonly items: readonly { readonly effectiveDeadlineAt: string }[]
}

function roleLabel(role: string) {
  return role.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (value) => value.toUpperCase())
}

function formatDeadline(value: string | null | undefined) {
  if (value === null || value === undefined) return '–'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '–'
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function departmentName(person: TeamPerson, departments: readonly Department[]) {
  return departments.find((department) => department.id === person.departmentId)?.name ?? 'Unassigned'
}

function nextTaskDeadline(tasks: readonly { readonly effectiveDeadlineAt: string }[]) {
  return tasks[0]?.effectiveDeadlineAt ?? null
}

export function Team() {
  usePageTitle('Team')
  const { api } = useAuth()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [departmentId, setDepartmentId] = useState<string | null>(null)
  const debouncedSearch = useDebounced(search)

  const usersQuery = useQuery({
    queryKey: ['team-users', debouncedSearch, departmentId],
    queryFn: () => {
      const params = new URLSearchParams()
      const q = debouncedSearch.trim()
      if (q !== '') params.set('q', q)
      if (departmentId !== null) params.set('departmentId', departmentId)
      const suffix = params.toString() === '' ? '' : `?${params.toString()}`
      return api.get<TeamPerson[]>(`/api/users${suffix}`)
    },
  })

  const departmentsQuery = useQuery({
    queryKey: ['team-departments'],
    queryFn: () => api.get<Department[]>('/api/departments'),
  })

  const people = usersQuery.data ?? []
  const workloadQueries = useQueries({
    queries: people.map((person) => ({
      queryKey: ['team-workload', person.id],
      enabled: usersQuery.isSuccess,
      queryFn: () => api.get<Workload>(`/api/tasks/workload?assigneeId=${encodeURIComponent(person.id)}`),
    })),
  })

  const nextTaskQueries = useQueries({
    queries: people.map((person) => ({
      queryKey: ['team-next-task', person.id],
      enabled: usersQuery.isSuccess,
      queryFn: () => api.get<NextTaskPage>(`/api/tasks?assigneeId=${encodeURIComponent(person.id)}&pageSize=1`),
    })),
  })

  const selectedPerson = people.find((person) => person.id === selectedId) ?? null
  useEffect(() => {
    if (selectedId === null) return
    if (people.some((person) => person.id === selectedId)) return
    setSelectedId(null)
  }, [people, selectedId])

  const departments = departmentsQuery.data ?? []

  const workloadById = useMemo(
    () => new Map(people.map((person, index) => [person.id, workloadQueries[index]?.data ?? null])),
    [people, workloadQueries],
  )
  const nextTaskById = useMemo(
    () => new Map(people.map((person, index) => [person.id, nextTaskQueries[index]?.data ?? null])),
    [people, nextTaskQueries],
  )
  const grouped = useMemo(() => {
    const map = new Map<string, TeamPerson[]>()
    for (const person of people) {
      const key = person.departmentId ?? 'unassigned'
      const list = map.get(key) ?? []
      list.push(person)
      map.set(key, list)
    }
    return [...map.entries()].map(([key, members]) => ({
      id: key,
      name: members[0]?.departmentId === null ? 'Unassigned' : `${departmentName(members[0]!, departments)} team`,
      members,
    }))
  }, [departments, people])

  const loading =
    usersQuery.isPending ||
    departmentsQuery.isPending ||
    workloadQueries.some((query) => query.isPending) ||
    nextTaskQueries.some((query) => query.isPending)
  const failure = usersQuery.error ?? departmentsQuery.error ?? workloadQueries.find((query) => query.isError)?.error ?? nextTaskQueries.find((query) => query.isError)?.error

  if (loading) {
    return (
      <>
        <Topbar title="Team" eyebrow="Manager workspace" />
        <div className="page">
          <div className="page-section">
            <LoadingState label="Loading team" />
          </div>
        </div>
      </>
    )
  }

  if (failure !== undefined) {
    return (
      <>
        <Topbar title="Team" eyebrow="Manager workspace" />
        <div className="page">
          <div className="page-section">
            <ErrorState error={failure} onRetry={() => void usersQuery.refetch()} />
          </div>
        </div>
      </>
    )
  }

  if (people.length === 0) {
    return (
      <>
        <Topbar title="Team" eyebrow="Manager workspace" />
        <div className="page">
          <div className="page-section">
            <EmptyState title="No visible team members" hint="There is nobody to show in this organisation yet." />
          </div>
        </div>
      </>
    )
  }

  const selectedWorkload = selectedId === null ? null : (workloadById.get(selectedId) ?? null)
  const selectedNextTask = selectedId === null ? null : (nextTaskById.get(selectedId) ?? null)

  return (
    <>
      <Topbar title="Team" eyebrow="Manager workspace" />
      <div className="page">
        <div className="page-section" style={{ display: 'grid', gap: 'var(--o-space-5)', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(280px, 0.8fr)' }}>
          <div style={{ display: 'grid', gap: 'var(--o-space-4)' }}>
            <div style={{ display: 'grid', gap: 'var(--o-space-3)', marginBottom: 'var(--o-space-2)' }}>
              <Input aria-label="Search people" placeholder="Search people..." value={search} onChange={setSearch} />

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} role="group" aria-label="Department filter">
                <Button
                  type="button"
                  size="small"
                  kind={departmentId === null ? 'primary' : 'secondary'}
                  aria-pressed={departmentId === null}
                  onClick={() => setDepartmentId(null)}
                >
                  All departments
                </Button>
                {(departmentsQuery.data ?? []).map((department) => (
                  <Button
                    key={department.id}
                    type="button"
                    size="small"
                    kind={departmentId === department.id ? 'primary' : 'secondary'}
                    aria-pressed={departmentId === department.id}
                    onClick={() => setDepartmentId(department.id)}
                  >
                    {department.name}
                  </Button>
                ))}
              </div>
            </div>

            {grouped.map((group) => (
              <section key={group.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-4)' }}>
                <div className="card-label" style={{ marginBottom: 'var(--o-space-3)' }}>{group.name}</div>
                <div style={{ display: 'grid', gap: 'var(--o-space-2)' }}>
                  {group.members.map((person) => {
                    const workload = workloadById.get(person.id) ?? null
                    const nextTask = nextTaskById.get(person.id) ?? null
                    const isSelected = selectedId === person.id
                    return (
                      <button
                        key={person.id}
                        type="button"
                        onClick={() => setSelectedId(person.id)}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'auto minmax(0, 1fr) auto auto',
                          gap: 'var(--o-space-3)',
                          alignItems: 'center',
                          width: '100%',
                          padding: 'var(--o-space-3)',
                          borderRadius: 'var(--r-md)',
                          border: `1px solid ${isSelected ? 'var(--mauve-200)' : 'var(--border)'}`,
                          background: isSelected ? 'var(--brand-soft)' : 'var(--surface)',
                          textAlign: 'left',
                        }}
                      >
                        <Avatar text={person.name.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('')} size="small" aria-label={person.name} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--o-space-2)', flexWrap: 'wrap' }}>
                            <div style={{ fontWeight: 500, color: 'var(--fg)' }}>{person.name}</div>
                            <Chip label={roleLabel(person.role)} />
                          </div>
                          <Text color="secondary">{departmentName(person, departments)}</Text>
                        </div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-2)', textAlign: 'right' }}>
                          <div>{workload === null ? '–' : `${formatCount(workload.activeCount)} active ${workload.activeCount === 1 ? 'task' : 'tasks'}`}</div>
                          <div>{nextTask === null ? 'No deadline' : formatDeadline(nextTaskDeadline(nextTask.items))}</div>
                          {workload !== null && workload.leave !== null ? (
                            <div style={{ color: 'var(--fg-muted)' }}>
                              {workload.leave.current ? 'On leave now' : 'Upcoming leave'}: {workload.leave.type.toLowerCase()} ·{' '}
                              {workload.leave.startsAt.slice(0, 10)} to {workload.leave.endsAt.slice(0, 10)}
                            </div>
                          ) : null}
                        </div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-muted)' }}>
                          {person.isActive ? 'Active' : 'Inactive'}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </section>
            ))}
          </div>

          <aside style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--o-space-4)', alignSelf: 'start' }}>
            {selectedPerson === null || selectedWorkload === null ? (
              <EmptyState title="Select a person" hint="Choose a row to open the workload preview." />
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--o-space-3)', marginBottom: 'var(--o-space-4)' }}>
                  <Avatar text={selectedPerson.name.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('')} aria-label={selectedPerson.name} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--fg)' }}>{selectedPerson.name}</div>
                    <Text color="secondary">
                      {departmentName(selectedPerson, departments)} · {roleLabel(selectedPerson.role)}
                    </Text>
                  </div>
                </div>

                <div
                  style={{
                    background: 'var(--bg-subtle)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-md)',
                    padding: 'var(--o-space-3) var(--o-space-4)',
                    marginBottom: 'var(--o-space-4)',
                    fontSize: 12.5,
                    color: 'var(--fg-2)',
                  }}
                >
                  <div style={{ fontWeight: 500 }}>{selectedWorkload.activeCount} active {selectedWorkload.activeCount === 1 ? 'task' : 'tasks'}</div>
                  <div style={{ color: 'var(--fg-muted)' }}>
                    {selectedWorkload.dueNext24h} due in the next 24 hours · {selectedWorkload.dueNext7d} due this week
                    {selectedWorkload.blocked > 0 ? ` · ${selectedWorkload.blocked} blocked` : ''}
                  </div>
                  {selectedWorkload.leave !== null ? (
                    <div style={{ marginTop: 4, color: 'var(--fg-muted)' }}>
                      {selectedWorkload.leave.current ? 'On leave now' : 'Upcoming leave'}: {selectedWorkload.leave.type.toLowerCase()} · {selectedWorkload.leave.startsAt.slice(0, 10)} to {selectedWorkload.leave.endsAt.slice(0, 10)}
                    </div>
                  ) : null}
                </div>

                <div style={{ display: 'grid', gap: 'var(--o-space-2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--o-space-3)' }}>
                    <span style={{ color: 'var(--fg-muted)' }}>Next deadline</span>
                    <strong>{selectedNextTask === null ? 'No visible tasks' : formatDeadline(nextTaskDeadline(selectedNextTask.items))}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--o-space-3)' }}>
                    <span style={{ color: 'var(--fg-muted)' }}>Active tasks</span>
                    <strong>{formatCount(selectedWorkload.activeCount)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--o-space-3)' }}>
                    <span style={{ color: 'var(--fg-muted)' }}>Blocked</span>
                    <strong>{formatCount(selectedWorkload.blocked)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--o-space-3)' }}>
                    <span style={{ color: 'var(--fg-muted)' }}>Due in 24h</span>
                    <strong>{formatCount(selectedWorkload.dueNext24h)}</strong>
                  </div>
                </div>
              </>
            )}
          </aside>
        </div>
      </div>
    </>
  )
}
